import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry, shareReplay } from 'rxjs/operators';
import { DivisionClassValidationResponse } from '../../models/sa';
import { GET_SHOW_DIVISIONS } from '../constants/api-endpoints';
import { LoggerService } from './logger.service';

interface CacheEntry {
  /** The in-flight *or* settled request. Caching the observable rather than the
   *  value is what collapses concurrent callers onto one HTTP call — the entry
   *  wizard asks for this response twice in the same `forkJoin` (once for the
   *  division catalogue, once for the quantity rules) and the payload is ~400 KB. */
  response$: Observable<DivisionClassValidationResponse>;
  timestamp: number;
}

/** Cache time-to-live in milliseconds */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
/** Number of retry attempts for failed API calls */
const RETRY_ATTEMPTS = 2;
/** Delay between retry attempts in milliseconds */
const RETRY_DELAY_MS = 1000;

/**
 * Service for calling the DivisionClassValidation API
 * with caching and retry logic
 */
@Injectable({ providedIn: 'root' })
export class ValidationApiService {
  private readonly cache = new Map<string, CacheEntry>();

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService
  ) {}

  /**
   * Get validation data for an entry
   * @param entryUuid Entry UUID
   * @param show Show abbreviation (default: 'sa')
   * @returns Observable of validation response
   */
  getValidationData(entryUuid: string, show: string = 'sa'): Observable<DivisionClassValidationResponse> {
    if (!entryUuid || entryUuid === 'new') {
      return throwError(() => new Error('Invalid entryUuid'));
    }

    const key = this.cacheKey(entryUuid, show);

    // Check cache first
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      this.logger.debug(`[ValidationApiService] Cache hit for entry: ${entryUuid}`);
      return cached.response$;
    }

    // Cache miss - make API call
    this.logger.debug(`[ValidationApiService] Cache miss, calling API for entry: ${entryUuid}`);
    const response$ = this.request(show, entryUuid).pipe(
      retry({
        count: RETRY_ATTEMPTS,
        // A 4xx won't change on retry.
        delay: (error: HttpErrorResponse) =>
          error.status >= 400 && error.status < 500 ? throwError(() => error) : timer(RETRY_DELAY_MS)
      }),
      // Replays the one response to every subscriber, so the second caller in a
      // forkJoin joins the in-flight request instead of starting its own.
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((error) => {
        // shareReplay would otherwise replay this failure to every later caller
        // for the whole TTL, so drop the entry and let the next one retry.
        this.cache.delete(key);
        this.logger.error(`[ValidationApiService] API call failed for entry: ${entryUuid}`, error);
        return throwError(() => error);
      })
    );

    this.cache.set(key, { response$, timestamp: Date.now() });
    return response$;
  }

  private request(show: string, entryUuid: string): Observable<DivisionClassValidationResponse> {
    const url = GET_SHOW_DIVISIONS(show);
    return this.http.post<DivisionClassValidationResponse>(url, { entryUuid }).pipe(
      // An entry with no exhibitor (a pass) is rejected when keyed; the
      // entry-less catalogue is what the backend would compute for it anyway.
      catchError((error: HttpErrorResponse) =>
        error.status === 404 && error.error?.errorDetails === 'ExhibitorDetailsNotFound'
          ? this.http.post<DivisionClassValidationResponse>(url, {})
          : throwError(() => error)
      )
    );
  }

  private cacheKey(entryUuid: string, show: string): string {
    return `${show}::${entryUuid}`;
  }

  /**
   * Clear cache for a specific entry (call after save/update)
   * @param entryUuid Entry UUID to clear from cache
   */
  clearCache(entryUuid: string): void {
    // Keyed by show *and* entry, so clear every show's copy of this entry.
    for (const key of Array.from(this.cache.keys())) {
      if (key.endsWith(`::${entryUuid}`)) {
        this.cache.delete(key);
        this.logger.debug(`[ValidationApiService] Cleared cache for entry: ${entryUuid}`);
      }
    }
  }

  /**
   * Clear all cached validation data
   */
  clearAllCache(): void {
    this.cache.clear();
    this.logger.debug('[ValidationApiService] Cleared all cache');
  }
}
